import { useState } from "react";
import type { ReactNode } from "react";
import type { Event, TicketTier } from "../lib/types";
import {
  formatEventDate,
  formatEventTimeRange,
  formatTicketPrice,
  getEventStatusLabel,
  getFlyerFallbackLabel,
  getHostedByNames,
  getTierCallToAction
} from "../lib/public-marketplace";

export function PublicPageIntro({
  eyebrow,
  title,
  description,
  actions
}: {
  eyebrow: string;
  title: string;
  description: string;
  actions?: ReactNode;
}) {
  return (
    <header className="marketplace-intro">
      <div>
        <p className="marketplace-intro__eyebrow">{eyebrow}</p>
        <h1>{title}</h1>
        <p className="marketplace-intro__copy">{description}</p>
      </div>
      {actions && <div className="marketplace-intro__actions">{actions}</div>}
    </header>
  );
}

export function EmptyState({
  title,
  body,
  action
}: {
  title: string;
  body: string;
  action?: React.ReactNode;
}) {
  return (
    <section className="marketplace-empty-state">
      <p className="marketplace-intro__eyebrow">AnTix</p>
      <h2>{title}</h2>
      <p>{body}</p>
      {action}
    </section>
  );
}

export function EventFlyer({ event, priority = false, compact = false }: { event: Pick<Event, "title" | "flyerImageUrl">; priority?: boolean; compact?: boolean }) {
  const [failed, setFailed] = useState(false);
  const showImage = Boolean(event.flyerImageUrl) && !failed;

  return (
    <div className={compact ? "marketplace-flyer marketplace-flyer-compact" : "marketplace-flyer"}>
      {showImage ? (
        <img
          src={event.flyerImageUrl}
          alt={`${event.title} flyer`}
          loading={priority ? "eager" : "lazy"}
          onError={() => setFailed(true)}
        />
      ) : (
        <div className="marketplace-flyer__fallback" aria-label={`${event.title} flyer unavailable`}>
          <span>{getFlyerFallbackLabel(event.title)}</span>
        </div>
      )}
    </div>
  );
}

export function HostedBy({ hostedBy }: { hostedBy?: string[] }) {
  const names = getHostedByNames(hostedBy);
  if (names.length === 0) return null;
  return (
    <p className="marketplace-hosted-by">
      <span>Hosted by</span> {names.join(", ")}
    </p>
  );
}

export function EventMeta({ event }: { event: Pick<Event, "startsAt" | "endsAt" | "venueName" | "venueAddress" | "ageRequirement" | "timezone"> }) {
  return (
    <div className="marketplace-meta">
      <div>
        <span>Date</span>
        <strong>{formatEventDate(event.startsAt, event.timezone)}</strong>
      </div>
      <div>
        <span>Time</span>
        <strong>{formatEventTimeRange(event.startsAt, event.endsAt, event.timezone)}</strong>
      </div>
      {event.venueName && (
        <div>
          <span>Venue</span>
          <strong>{event.venueName}</strong>
          {event.venueAddress && <small>{event.venueAddress}</small>}
        </div>
      )}
      {event.ageRequirement && (
        <div>
          <span>Entry</span>
          <strong>{event.ageRequirement}</strong>
        </div>
      )}
    </div>
  );
}

export function SaleStatusBadge({ event, isPrevious = false }: { event: Pick<Event, "status" | "salesStatus">; isPrevious?: boolean }) {
  const label = getEventStatusLabel(event, isPrevious);
  const tone =
    label === "On Sale" ? "marketplace-status marketplace-status-live" : "marketplace-status marketplace-status-muted";
  return <span className={tone}>{label}</span>;
}

export function TicketTierPurchaseRow({
  event,
  tier,
  isPrevious = false
}: {
  event: Pick<Event, "slug" | "status" | "salesStatus" | "capacity">;
  tier: Pick<TicketTier, "id" | "name" | "priceCents" | "currency" | "status" | "salesStatus" | "checkoutUrl" | "inventoryLimit" | "saleStartsAt" | "saleEndsAt">;
  isPrevious?: boolean;
}) {
  const cta = getTierCallToAction(event, tier, event.slug ? `/e/${event.slug}` : undefined, isPrevious);

  return (
    <div className="tier-purchase-row">
      <div className="tier-purchase-row__copy">
        <strong>{tier.name}</strong>
        <span>{formatTicketPrice(tier.priceCents, tier.currency)}</span>
      </div>
      {cta.href ? (
        <a
          className="tier-purchase-row__button"
          href={cta.href}
        >
          {cta.label}
        </a>
      ) : (
        <span className="tier-purchase-row__button tier-purchase-row__button-disabled" aria-disabled="true">
          {cta.label}
        </span>
      )}
    </div>
  );
}

export function TicketTierList({
  event,
  tiers,
  isPrevious = false
}: {
  event: Pick<Event, "slug" | "status" | "salesStatus" | "capacity">;
  tiers?: TicketTier[];
  isPrevious?: boolean;
}) {
  if (!tiers || tiers.length === 0) {
    return (
      <div className="marketplace-tier-list">
        <div className="tier-purchase-row">
          <div className="tier-purchase-row__copy">
            <strong>Tickets unavailable</strong>
            <span>Check back soon for the next drop.</span>
          </div>
          <span className="tier-purchase-row__button tier-purchase-row__button-disabled" aria-disabled="true">
            Unavailable
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className="marketplace-tier-list">
      {tiers.map((tier) => (
        <TicketTierPurchaseRow key={tier.id} event={event} tier={tier} isPrevious={isPrevious} />
      ))}
    </div>
  );
}
